import {
  dripMediumOptions,
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'

const REQUIRED_SEQUENCE_TAGS = [
  '@[hotelName](__hotelName__)',
  '@[guestName](__guestName__)'
]

export const DRIP_SEQUENCE_STEP_IDS = {
  step1: 'step1',
  step2: 'step2',
  step3: 'step3'
}

export const MAX_FOLLOW_UP_STEPS = 2

export const MAX_SEQUENCE_STEPS = MAX_FOLLOW_UP_STEPS + 1

export const SEQUENCE_CHANNEL_STATUS = {
  empty: 'empty',
  needsRefinement: 'needsRefinement',
  ready: 'ready'
}

const DEFAULT_FOLLOW_UP_DELAY_HOURS = 120

const DEFAULT_MESSAGE_TEMPLATE =
  'Hi @[guestName](__guestName__), we would love to welcome you back to @[hotelName](__hotelName__)! Book your next stay with us.'

const DEFAULT_FOLLOW_UP_1_TEMPLATE =
  'Hi @[guestName](__guestName__), just a friendly reminder from @[hotelName](__hotelName__) — we have a special offer waiting for you.'

const DEFAULT_FOLLOW_UP_2_TEMPLATE =
  'Hi @[guestName](__guestName__), this is our final reminder from @[hotelName](__hotelName__). We hope to see you soon!'

export function convertToHours(value, unit) {
  return unit === 'days' ? value * 24 : value
}

export function convertFromHours(hours) {
  if (!hours) return { value: 2, unit: 'days', displayValue: 2 }
  if (hours % 24 === 0) {
    return { value: hours, unit: 'days', displayValue: hours / 24 }
  }
  return { value: hours, unit: 'hours', displayValue: hours }
}

export function getConditionLabel(hours) {
  const { unit, displayValue } = convertFromHours(hours)
  const singularUnit = unit === 'days' ? 'day' : 'hour'
  return {
    value: displayValue,
    unit: displayValue === 1 ? singularUnit : unit
  }
}

export function createDefaultSequenceSteps(campaignData = {}) {
  const medium = campaignData.medium || dripMediumOptions.EmailAndText
  const hasEmail = dripMediumOptionsWithEmail.includes(medium)
  const messageTemplate = campaignData.messageTemplate || DEFAULT_MESSAGE_TEMPLATE
  const messageMedia = campaignData.messageMediaUrls?.[0] ?? null

  return [
    {
      id: DRIP_SEQUENCE_STEP_IDS.step1,
      title: 'Initial Message',
      isActive: true,
      hoursAfterPreviousCampaign: 0,
      medium,
      messageTemplate,
      messageMedia,
      hasEmailCampaign: hasEmail,
      isInitialStep: true,
      emailTemplateId: null,
      emailTemplateName: '',
      emailTemplateDescription: '',
      isEmailContentReviewed: false,
      isTextContentReviewed: Boolean(campaignData.messageTemplate)
    },
    {
      id: DRIP_SEQUENCE_STEP_IDS.step2,
      title: 'Follow-up 1',
      isActive: true,
      hoursAfterPreviousCampaign: 48,
      medium,
      messageTemplate: DEFAULT_FOLLOW_UP_1_TEMPLATE,
      messageMedia: null,
      hasEmailCampaign: hasEmail,
      isInitialStep: false,
      emailTemplateId: null,
      emailTemplateName: '',
      emailTemplateDescription: '',
      isEmailContentReviewed: false,
      isTextContentReviewed: false
    }
  ]
}

export function createFollowUpStep(steps = [], medium) {
  const usedNumbers = steps
    .map(step => Number(String(step.id).replace('step', '')))
    .filter(number => Number.isFinite(number))
  const nextNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 2

  return {
    id: `step${nextNumber}`,
    title: `Follow-up ${Math.max(steps.length, 1)}`,
    isActive: true,
    hoursAfterPreviousCampaign: DEFAULT_FOLLOW_UP_DELAY_HOURS,
    medium,
    messageTemplate: DEFAULT_FOLLOW_UP_2_TEMPLATE,
    messageMedia: null,
    hasEmailCampaign: dripMediumOptionsWithEmail.includes(medium),
    isInitialStep: false,
    emailTemplateId: null,
    emailTemplateName: '',
    emailTemplateDescription: '',
    isEmailContentReviewed: false,
    isTextContentReviewed: false
  }
}

export function canAddFollowUpStep(steps = []) {
  return Array.isArray(steps) && steps.length < MAX_SEQUENCE_STEPS
}

export function addFollowUpStep(steps = [], medium) {
  if (!canAddFollowUpStep(steps)) {
    return steps
  }

  return [...steps, createFollowUpStep(steps, medium)]
}

export function getFollowUpSteps(steps = []) {
  return Array.isArray(steps) ? steps.slice(1) : []
}

export function removeAllFollowUpSteps(steps = []) {
  return Array.isArray(steps) ? steps.slice(0, 1) : steps
}

export function removeSequenceStep(steps = [], stepId) {
  const target = steps.find(step => step.id === stepId)

  if (!target || target.isInitialStep) {
    return steps
  }

  return steps.filter(step => step.id !== stepId)
}

export function normalizeSequenceSteps(steps, campaignData) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return createDefaultSequenceSteps(campaignData)
  }

  return steps
    .map((step, index) => ({
      ...step,
      isInitialStep: step.isInitialStep ?? index === 0
    }))
    .filter(step => step.isInitialStep || step.isActive !== false)
    .slice(0, MAX_SEQUENCE_STEPS)
    .map(step => ({
      ...step,
      isActive: true,
      messageMedia: step.messageMedia ?? step.messageMediaUrls?.[0] ?? null,
      emailTemplateId: step.emailTemplateId ?? null,
      emailTemplateName: step.emailTemplateName ?? '',
      emailTemplateDescription: step.emailTemplateDescription ?? '',
      isEmailContentReviewed: Boolean(step.isEmailContentReviewed),
      isTextContentReviewed: step.isTextContentReviewed ?? Boolean(step.messageTemplate)
    }))
}

export function updateSequenceStep(steps = [], stepId, changes) {
  return steps.map(step => (step.id === stepId ? { ...step, ...changes } : step))
}

export function getSequenceStepTitle(step, index) {
  if (index === 0 || step?.isInitialStep) {
    return 'Initial Message'
  }

  return `Follow-up ${index}`
}

export function getSequenceStepSubtitle(step, index) {
  if (index === 0 || step?.isInitialStep) {
    return 'The first message sent when your campaign begins.'
  }

  const { value, unit } = getConditionLabel(step?.hoursAfterPreviousCampaign)
  const reference = index === 1 ? 'the initial message' : 'the previous message'

  return `Sent ${value} ${unit} after ${reference}.`
}

export function getTextChannelStatus(step) {
  const template = step?.messageTemplate || ''

  if (!template.trim()) {
    return SEQUENCE_CHANNEL_STATUS.empty
  }

  const hasRequiredTags = REQUIRED_SEQUENCE_TAGS.every(tag => template.includes(tag))

  if (!hasRequiredTags || !step?.isTextContentReviewed) {
    return SEQUENCE_CHANNEL_STATUS.needsRefinement
  }

  return SEQUENCE_CHANNEL_STATUS.ready
}

export function getEmailChannelStatus(step) {
  if (!step?.emailTemplateId) {
    return SEQUENCE_CHANNEL_STATUS.empty
  }

  if (!step?.isEmailContentReviewed) {
    return SEQUENCE_CHANNEL_STATUS.needsRefinement
  }

  return SEQUENCE_CHANNEL_STATUS.ready
}

export function replaceSequenceTags(text, { userName, hotelName, isDemoMode }) {
  if (!text) return ''

  const firstName = userName?.substring(0, userName?.indexOf(' ')) || 'Guest'
  const lastName = userName?.split(' ').pop() || ''
  const displayHotelName = isDemoMode ? 'The Luxury Resort & Spa' : hotelName

  return text
    .replace(/@\[(\S+)\]\((__)?(\S+?)(__)?\)/gm, '$1')
    .replace(/guestLastName/g, lastName)
    .replace(/guestName/g, firstName)
    .replace(/hotelName/g, displayHotelName)
    .replace(/promoName/g, 'Special Offer')
}

export function getStepDialogTitle(stepId, steps) {
  if (Array.isArray(steps)) {
    const index = steps.findIndex(step => step.id === stepId)

    if (index >= 0) {
      return getSequenceStepTitle(steps[index], index)
    }
  }

  switch (stepId) {
    case DRIP_SEQUENCE_STEP_IDS.step1:
      return 'Initial Message'
    case DRIP_SEQUENCE_STEP_IDS.step2:
      return 'Follow-up 1'
    case DRIP_SEQUENCE_STEP_IDS.step3:
      return 'Follow-up 2'
    default:
      return 'Edit step'
  }
}

export function syncPrimaryStepToCampaign(steps) {
  const initialStep = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step1)
  if (!initialStep) return {}

  return {
    messageTemplate: initialStep.messageTemplate,
    messageMediaUrls: initialStep.messageMedia ? [initialStep.messageMedia] : [],
    medium: initialStep.medium
  }
}

export function applyMediumToSteps(steps, medium) {
  if (!Array.isArray(steps)) return steps

  const hasEmail = dripMediumOptionsWithEmail.includes(medium)

  return steps.map(step => ({
    ...step,
    medium,
    hasEmailCampaign: hasEmail
  }))
}

export function validateSequenceSteps(steps, medium) {
  const errors = {}

  if (!Array.isArray(steps) || steps.length === 0) {
    return { errors, isValid: false }
  }

  const includesText = dripMediumOptionsWithText.includes(medium)

  steps.forEach(step => {
    if (!step.isActive) return

    if (includesText) {
      const template = step.messageTemplate || ''

      if (!template.trim()) {
        errors[step.id] = 'Add a text message for this step.'
        return
      }

      const missingTag = REQUIRED_SEQUENCE_TAGS.some(tag => !template.includes(tag))
      if (missingTag) {
        errors[step.id] =
          'Include the guest name and hotel name tags in the text message.'
      }
    }
  })

  const initialStep = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step1)
  if (!initialStep?.isActive) {
    errors[DRIP_SEQUENCE_STEP_IDS.step1] = 'The initial message must be active.'
  }

  return { errors, isValid: Object.keys(errors).length === 0 }
}
